import React, { useRef, useState } from "react";
import { Link2, Upload, FileText, Trash2, Sparkles, Download, ChevronDown, ChevronUp, AlertTriangle, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { argus, aErr, aH, Btn, Label, inputCls } from "@/components/argus/ui";

function Facts({ x }) {
  const List = ({ title, items, render }) => items?.length ? (
    <div className="mt-3"><Label>{title}</Label><ul className="mt-1 space-y-1 text-sm text-white/80">{items.map((it, i) => <li key={i}>• {render ? render(it) : it}</li>)}</ul></div>
  ) : null;
  return (
    <div className="mt-3 p-3 rounded-md bg-black/30 border border-white/5" data-testid="argus-facts">
      <p className="text-sm text-white/85">{x.summary}</p>
      <List title="Claims" items={x.claims} />
      <List title="People" items={x.people} render={p => `${p.name}${p.role ? ` · ${p.role}` : ""}`} />
      <List title="Organisations" items={x.organizations} />
      <List title="Money" items={x.money} render={m => `${m.amount} · ${m.context}`} />
      <List title="Dates" items={x.dates} render={d => `${d.date}: ${d.event}`} />
      <List title="Quotes" items={x.quotes} render={q => `"${q.quote}" ${q.speaker ? `— ${q.speaker}` : ""}`} />
      {x.red_flags?.length > 0 && <div className="mt-3 p-2 rounded bg-red-500/10 border border-red-400/20"><Label>Red flags</Label><ul className="text-sm text-red-200 mt-1">{x.red_flags.map((r, i) => <li key={i}>• {r}</li>)}</ul></div>}
    </div>
  );
}

function SourceRow({ s, onChange, onDelete }) {
  const [open, setOpen] = useState(false); const [busy, setBusy] = useState(""); const [err, setErr] = useState(""); const [vr, setVr] = useState(null);
  const verify = async () => { setBusy("v"); try { setVr((await argus.get(`/sources/${s.source_id}/verify`)).data); } catch (e) { setErr(aErr(e)); } finally { setBusy(""); } };
  const extract = async () => {
    setBusy("x"); setErr("");
    try { const { data } = await argus.post(`/sources/${s.source_id}/extract`, {}); onChange({ ...s, extracted: data }); setOpen(true); }
    catch (e) { setErr(aErr(e)); } finally { setBusy(""); }
  };
  const download = async () => {
    setBusy("d");
    try {
      const r = await api.get(`/argus/sources/${s.source_id}/file`, { headers: aH(), responseType: "blob" });
      const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = s.filename || "document"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { setErr("Download failed"); } finally { setBusy(""); }
  };
  const Icon = s.kind === "link" ? Link2 : s.kind === "file" ? Upload : FileText;
  return (
    <div className="p-3 rounded-lg argus-glass" data-testid={`argus-source-${s.source_id}`}>
      <div className="flex items-start gap-3 flex-wrap">
        <Icon className="w-4 h-4 text-[#10b981] mt-0.5 shrink-0" />
        <div className="flex-1 min-w-[160px]">
          <div className="font-semibold text-sm break-words">{s.title}</div>
          <div className="text-[11px] text-white/45 break-all">{s.url || s.filename || "Pasted text"} · {(s.chars || 0).toLocaleString()} chars · {s.added_by}</div>
          {s.scanned && <div className="text-[11px] text-amber-300 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Looks like a scanned PDF, so no text could be read.</div>}
        </div>
        <div className="flex gap-1.5 flex-wrap">
          {s.extracted ? (
            <Btn kind="ghost" onClick={() => setOpen(o => !o)} data-testid={`argus-facts-toggle-${s.source_id}`}>{open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />} Facts</Btn>
          ) : (
            <Btn busy={busy === "x"} onClick={extract} data-testid={`argus-extract-${s.source_id}`}><Sparkles className="w-3.5 h-3.5" /> Extract facts</Btn>
          )}
          {s.storage_path && <Btn kind="ghost" busy={busy === "d"} onClick={download} data-testid={`argus-download-${s.source_id}`}><Download className="w-3.5 h-3.5" /></Btn>}
          {s.sha256 && <Btn kind="ghost" busy={busy === "v"} onClick={verify} data-testid={`argus-verify-${s.source_id}`}><ShieldCheck className="w-3.5 h-3.5" /> Verify</Btn>}
          <Btn kind="danger" onClick={() => onDelete(s)} data-testid={`argus-source-del-${s.source_id}`}><Trash2 className="w-3.5 h-3.5" /></Btn>
        </div>
      </div>
      {err && <div className="text-red-300 text-xs mt-2">{err}</div>}
      {vr && <div className={`text-xs mt-2 ${vr.intact ? "text-emerald-300" : "text-red-300"}`} data-testid={`argus-verify-result-${s.source_id}`}>{vr.intact ? `Fingerprint matches: unaltered since ${new Date(vr.received_at).toLocaleString()}` : "WARNING: file has changed since it was added"} <span className="font-mono text-white/30">#{vr.sha256.slice(0, 16)}</span></div>}
      {open && s.extracted && <Facts x={s.extracted} />}
    </div>
  );
}

export default function ArgusSources({ caseId, sources, setSources }) {
  const [mode, setMode] = useState("link");
  const [url, setUrl] = useState(""); const [title, setTitle] = useState(""); const [text, setText] = useState("");
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(""); const [pct, setPct] = useState(0);
  const fileRef = useRef(null);
  const add = (s) => setSources(ss => [s, ...ss]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      if (mode === "link") { const { data } = await argus.post(`/cases/${caseId}/sources/link`, { url, title: title || null }); add(data); setUrl(""); }
      else { const { data } = await argus.post(`/cases/${caseId}/sources/text`, { title: title || "Notes", text }); add(data); setText(""); }
      setTitle("");
    } catch (e) { setErr(aErr(e, "Could not add source")); } finally { setBusy(false); }
  };
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { setErr("File is larger than 20 MB"); return; }
    setBusy(true); setErr(""); setPct(0);
    const fd = new FormData(); fd.append("file", file); fd.append("title", title);
    try {
      const { data } = await argus.post(`/cases/${caseId}/sources/upload`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 300000, onUploadProgress: (e) => setPct(Math.round((e.loaded / (e.total || 1)) * 100)) });
      add(data); setTitle("");
    } catch (e) { setErr(aErr(e, "Upload failed")); } finally { setBusy(false); setPct(0); if (fileRef.current) fileRef.current.value = ""; }
  };
  const del = async (s) => {
    if (!window.confirm(`Remove "${s.title}" from this case?`)) return;
    await argus.del(`/sources/${s.source_id}`); setSources(ss => ss.filter(x => x.source_id !== s.source_id));
  };

  return (
    <div className="space-y-4" data-testid="argus-sources">
      <div className="p-4 rounded-lg argus-glass">
        <div className="flex gap-1 mb-3">
          {[["link", "Link"], ["upload", "Upload file"], ["text", "Paste text"]].map(([id, l]) => (
            <button key={id} type="button" onClick={() => setMode(id)} className={`px-3 py-1 rounded text-xs ${mode === id ? "bg-[#10b981] text-black font-semibold" : "text-white/60 hover:text-white"}`} data-testid={`argus-src-mode-${id}`}>{l}</button>
          ))}
        </div>
        <input className={inputCls} value={title} onChange={e => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="argus-src-title" />
        {mode === "link" && <input className={`${inputCls} mt-2`} value={url} onChange={e => setUrl(e.target.value)} placeholder="https://… article, report, press release" data-testid="argus-src-url" />}
        {mode === "text" && <textarea className={`${inputCls} mt-2 min-h-[120px]`} value={text} onChange={e => setText(e.target.value)} placeholder="Paste interview notes, transcripts, emails…" data-testid="argus-src-text" />}
        {mode === "upload" ? (
          <label className="mt-2 flex items-center justify-center gap-2 border border-dashed border-white/20 rounded-md py-6 text-sm text-white/60 cursor-pointer hover:border-[#10b981]/60" data-testid="argus-src-drop">
            <Upload className="w-4 h-4" /> {busy ? `Uploading… ${pct}%` : "Choose PDF, DOCX, TXT, MD, CSV or HTML (max 20 MB)"}
            <input ref={fileRef} type="file" accept=".pdf,.docx,.txt,.md,.csv,.html" className="hidden" disabled={busy} onChange={e => upload(e.target.files?.[0])} data-testid="argus-src-file" />
          </label>
        ) : (
          <Btn busy={busy} onClick={submit} className="mt-2" disabled={mode === "link" ? url.trim().length < 4 : text.trim().length < 10} data-testid="argus-src-add">Add source · free</Btn>
        )}
        {err && <div className="text-red-300 text-xs mt-2" data-testid="argus-src-err">{err}</div>}
        <div className="text-[11px] text-white/40 mt-2">Adding and reading sources is free. "Extract facts" uses 1 AI action.</div>
      </div>
      {sources.length === 0 ? <div className="text-sm text-white/45">No sources yet. Add your first link, document or notes above.</div>
        : sources.map(s => <SourceRow key={s.source_id} s={s} onDelete={del} onChange={(n) => setSources(ss => ss.map(x => x.source_id === n.source_id ? n : x))} />)}
    </div>
  );
}
